"use client";
import useTheme from "../../hooks/useTheme";
import { isDarkTheme } from "../../../theme";

import { useState, useRef, useEffect, useMemo } from "react";
import { BsSend } from "react-icons/bs";
import EmojiPicker, { Theme } from "emoji-picker-react";

import { CgAttachment } from "react-icons/cg";
import { AiOutlineClose } from "react-icons/ai";
import { FiSmile } from "react-icons/fi";
import toast from "react-hot-toast";
import RichTextInput from "../../common/rich-text/RichTextInput";
import { richTextToPlain } from "../../common/rich-text/richText";
import useSendMessage from "../../hooks/useSendMessage";
import parseMentions from "../../common/ParseMentions";
import { useSelector, useDispatch } from "react-redux";
import { setDraft } from "../../Slices/ChatSlice";


let typingTimeout;
let typingIndicatorTimeout;

const MessageInput = ({
  droppedFile,
  clearDroppedFile,
  replyingTo,
  setReplyingTo,
  editingMessage,
  setEditingMessage,
  selectedConversationData,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const drafts = useSelector((state) => state.conversation.drafts);

  // Identifies which conversation an unsent draft belongs to; null when no
  // conversation is selected, in which case there is nothing to save against.
  const draftKey =
    selectedConversationData?.selectedConversation?.value != null
      ? `${selectedConversationData?.conversationType || "simple"}:${
          selectedConversationData.selectedConversation.value
        }`
      : null;

  const [message, setMessage] = useState(
    () => (draftKey && drafts[draftKey]) || ""
  );
  const [attachedFile, setAttachedFile] = useState(null);
  const [existingFile, setExistingFile] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);

  const inputRef = useRef(null);
  const editorApiRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const fileInputRef = useRef(null);
  const { loading, sendmessage, editMessage } = useSendMessage();
  const loggedInUser = useSelector(
    (state) => state?.data?.userInfo?.data?.data
  );
  const loggedInUserId = loggedInUser?.UserId;
  const selectedGroupParticipants =
    selectedConversationData?.selectedConversation?.participants || [];
  const filteredParticipants = selectedGroupParticipants.filter(
    (participant) => participant.userId !== loggedInUserId
  );
  const connection = useSelector((state) => state.socket.socketConnection);

  // Preview URL for a staged image attachment. Built once per file (and
  // revoked when the file changes or the input unmounts) rather than inline
  // in the JSX below — `URL.createObjectURL` in render allocates a brand new
  // blob URL on every single re-render (every keystroke, every typing tick)
  // and none of them are ever released, which leaks for as long as the
  // attachment stays staged.
  const attachedFilePreviewUrl = useMemo(() => {
    if (!attachedFile || !attachedFile.type?.startsWith("image/")) return null;
    return URL.createObjectURL(attachedFile);
  }, [attachedFile]);

  useEffect(() => {
    if (!attachedFilePreviewUrl) return;
    return () => URL.revokeObjectURL(attachedFilePreviewUrl);
  }, [attachedFilePreviewUrl]);

  // Latest-value mirrors so the draft save effects below can read the current
  // text/conversation without listing them as dependencies (which would make
  // those effects re-run on every keystroke instead of only on switch/close).
  const messageRef = useRef(message);
  messageRef.current = message;
  const draftKeyRef = useRef(draftKey);
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  const isEditingRef = useRef(false);
  isEditingRef.current = !!editingMessage;
  // Tracks edit mode across renders so entering/leaving it can be detected.
  const wasEditingRef = useRef(false);

  // Switching conversations: park the draft for the one being left and load
  // the one being opened. Without this the text stays in the box and would be
  // sent to whoever is selected next.
  useEffect(() => {
    const previousKey = draftKeyRef.current;
    if (previousKey === draftKey) return;

    if (previousKey && !isEditingRef.current) {
      dispatch(setDraft({ key: previousKey, value: messageRef.current }));
    }
    draftKeyRef.current = draftKey;
    setMessage(draftKey ? draftsRef.current[draftKey] || "" : "");
  }, [draftKey, dispatch]);

  // Closing the chat modal unmounts this input, so save on the way out too.
  useEffect(() => {
    return () => {
      const key = draftKeyRef.current;
      if (key && !isEditingRef.current) {
        dispatch(setDraft({ key, value: messageRef.current }));
      }
    };
  }, [dispatch]);

  useEffect(() => {
    if (editingMessage) {
      // Entering edit mode replaces the box contents with the message being
      // edited, so park the in-progress draft first — it is restored below
      // once the edit is submitted or cancelled.
      if (!wasEditingRef.current && draftKeyRef.current) {
        dispatch(
          setDraft({ key: draftKeyRef.current, value: messageRef.current })
        );
      }
      wasEditingRef.current = true;
      setMessage(editingMessage.content || "");
      if (editingMessage.fileName) {
        fetchExistingFile(editingMessage.id);
      } else {
        setExistingFile(null);
      }
      if (inputRef.current) {
        const focusTimer = setTimeout(() => {
          const editor = inputRef.current;
          if (!editor) return;
          editor.focus();
          const range = document.createRange();
          range.selectNodeContents(editor);
          range.collapse(false);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
          editor.scrollTop = editor.scrollHeight;
        }, 0);
        return () => clearTimeout(focusTimer);
      }
    } else {
      if (wasEditingRef.current) {
        const key = draftKeyRef.current;
        setMessage(key ? draftsRef.current[key] || "" : "");
      }
      wasEditingRef.current = false;
      setExistingFile(null);
    }
  }, [editingMessage, dispatch]);

  const fetchExistingFile = async (messageId) => {
    try {

      const mockFile = new File(["mock content"], editingMessage.fileName, {
        type: "application/pdf",
      });
      const mockUrl = URL.createObjectURL(mockFile);

      setExistingFile({
        file: mockFile,
        name: editingMessage.fileName,
        url: mockUrl,
      });
    } catch (error) {
      console.error("Error fetching existing file:", error);
      toast.error("Failed to load existing file");
    }
  };

  const startTyping = async (targetUser) => {
    const targetUserId = String(targetUser?.value);
    const userDetail = {
      fullName: loggedInUser?.FirstName + " " + loggedInUser?.LastName,
      typingUserId: loggedInUser?.UserId,
      targetUserOrGroupId: targetUserId,
      conversationType: selectedConversationData?.conversationType,
    };
    if (connection) {
      const res = await connection
        .invoke("Typing", targetUserId, userDetail)
        .catch((err) => console.error(err));
    }
  };

  const stopTyping = async (targetUser) => {
    const targetUserId = String(targetUser?.value);
    const userDetail = {
      fullName: loggedInUser?.FirstName + " " + loggedInUser?.LastName,
      typingUserId: loggedInUser?.UserId,
      targetUserOrGroupId: targetUserId,
      conversationType: selectedConversationData?.conversationType,
    };
    if (connection) {
      const res = await connection
        .invoke("StopTyping", targetUserId, userDetail)
        .catch((err) => console.error(err));
    }
  };

  useEffect(() => {
    if (droppedFile) {
      setAttachedFile(droppedFile);
      inputRef.current.focus();
      clearDroppedFile();
    }
  }, [droppedFile, clearDroppedFile]);

  useEffect(() => {
    if (replyingTo) {
      if (inputRef.current) {
        setTimeout(() => {
          inputRef.current.focus();
        }, 0);
      }
    }
  }, [replyingTo]);

  const handleContentChange = (e, newValue) => {
    const safeValue = newValue ?? e.target.value ?? "";
    setMessage(safeValue);

    // Only show typing indicator if message is not empty and not editing
    const trimmedMessage = richTextToPlain(safeValue).trim();
    
    if (trimmedMessage && !editingMessage && !isTyping) {
      setIsTyping(true);
      startTyping(selectedConversationData.selectedConversation);
    } else if (!trimmedMessage && isTyping && !editingMessage) {
      // Stop typing if message becomes empty
      setIsTyping(false);
      stopTyping(selectedConversationData.selectedConversation);
    }

    // Clear existing timeout
    clearTimeout(typingTimeout);

    // Only set timeout if there's content
    if (trimmedMessage) {
      typingTimeout = setTimeout(() => {
        setIsTyping(false);
        if (!editingMessage) {
          stopTyping(selectedConversationData.selectedConversation);
        }
      }, 2000);
    }
  };

  const handleSubmit = async () => {
    if (!richTextToPlain(message).trim() && !attachedFile && !existingFile) return;

    // Stop typing indicator immediately
    if (isTyping) {
      setIsTyping(false);
      stopTyping(selectedConversationData.selectedConversation);
    }

    // Clear typing timeout
    clearTimeout(typingTimeout);

    let formData;
    if (replyingTo) {
      formData = {
        Content: message,
        File: attachedFile,
        RepliedMessageId: replyingTo?.id,
        ReplyingTo: replyingTo,
      };
    } else if (editingMessage) {
      formData = {
        Content: message,
        File: attachedFile || (existingFile ? existingFile.file : null),
        MessageId: editingMessage?.id,
      };
    } else {
      formData = {
        Content: message,
        File: attachedFile,
      };
    }

    clearFile();
    setMessage("");
    // Once sent, the text is no longer a draft. Skipped when finishing an
    // edit, since in that case the effect above restores the compose draft
    // that was parked when edit mode started.
    if (!editingMessage && draftKey) {
      dispatch(setDraft({ key: draftKey, value: "" }));
    }
    setReplyingTo(null);
    setEditingMessage(null);
    setExistingFile(null);

    if (editingMessage) {
      await editMessage(formData, editingMessage);
    } else {
      await sendmessage(formData);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const maxFileSize = 10 * 1024 * 1024; // 10MB

      if (file.size > maxFileSize) {
        toast.error("File size exceeds 10MB. Please upload a smaller file.");
        return;
      }

      setAttachedFile(file);
      if (existingFile) {
        URL.revokeObjectURL(existingFile.url);
        setExistingFile(null);
      }
      inputRef.current.focus();
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData.items || [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file") {
        e.preventDefault();
        const file = item.getAsFile();
        const maxFileSize = 10 * 1024 * 1024; // 10MB

        if (file.size > maxFileSize) {
          toast.error("File size exceeds 10MB. Please upload a smaller file.");
          return;
        }

        setAttachedFile(file);
      }
    }
  };

  const clearFile = () => {
    setAttachedFile(null);
    if (existingFile) {
      URL.revokeObjectURL(existingFile.url);
      setExistingFile(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    inputRef.current.focus();
  };

  const handleCancelEdit = () => {
    // Stop typing indicator when cancelling edit
    if (isTyping) {
      setIsTyping(false);
      stopTyping(selectedConversationData.selectedConversation);
    }
    clearTimeout(typingTimeout);
    
    setMessage("");
    setEditingMessage(null);
    clearFile();
  };

  const handleEmojiClick = (emoji) => {
    editorApiRef.current?.insertText(emoji.emoji);

    setShowEmojiPicker(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === "Escape" && editingMessage) {
      handleCancelEdit();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        inputRef.current !== event.target
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  
  // Handle incoming typing notifications
  useEffect(() => {
    if (connection) {
      const handleTyping = (userDetails, isTyping) => {
        // Check if the typing user is in the current conversation
        const isGroupConversation =
          selectedConversationData?.conversationType === "group";
        const isSimpleConversation =
          selectedConversationData?.conversationType === "simple";

        // For group conversations, check if the typing user is a participant
        if (isGroupConversation) {
          if (
            (isTyping === true || isTyping === "true") &&
            loggedInUserId != userDetails?.typingUserId && // Avoid showing self-typing indicator
            selectedConversationData?.selectedConversation?.value ==
              userDetails?.targetUserOrGroupId // Ensure the typing event is for the current group
          ) {
            setTypingUsers((prev) => {
              // Add user to typing users if not already there
              if (!prev.some((user) => user.id === userDetails.typingUserId)) {
                return [
                  ...prev,
                  {
                    id: userDetails.typingUserId,
                    name: userDetails.fullName,
                    timestamp: Date.now(),
                  },
                ];
              }
              return prev;
            });
          } else if (isTyping === false || isTyping === "false") {
            // Remove user from typing users
            setTypingUsers((prev) =>
              prev.filter((user) => user.id !== userDetails.typingUserId)
            );
          }
        }

        // For simple (one-on-one) conversations, check if the typing user is the receiver
        if (isSimpleConversation) {
          if (
            (isTyping === true || isTyping === "true") &&
            loggedInUserId == userDetails?.targetUserOrGroupId && // Current user is the receiver
            selectedConversationData?.selectedConversation?.value ==
              userDetails?.typingUserId // Typing user is selected
          ) {
            setTypingUsers((prev) => {
              // Add user to typing users if not already there
              if (!prev.some((user) => user.id === userDetails.typingUserId)) {
                return [
                  ...prev,
                  {
                    id: userDetails.typingUserId,
                    name: userDetails.fullName,
                    timestamp: Date.now(),
                  },
                ];
              }
              return prev;
            });
          } else if (isTyping === false || isTyping === "false") {
            // Remove user from typing users
            setTypingUsers((prev) =>
              prev.filter((user) => user.id !== userDetails.typingUserId)
            );
          }
        }
      };

      connection.on("UserTyping", handleTyping);

      // Cleanup the event listener when the component unmounts
      return () => {
        connection.off("UserTyping", handleTyping);
      };
    }
  }, [
    connection,
    selectedGroupParticipants,
    selectedConversationData?.conversationType,
    loggedInUserId,
    selectedConversationData?.selectedConversation?.value,
  ]);

  // Cleanup effect for timeouts and typing status on unmount
  useEffect(() => {
    return () => {
      clearTimeout(typingTimeout);
      clearTimeout(typingIndicatorTimeout);
      // Stop typing indicator on component unmount
      if (isTyping && selectedConversationData?.selectedConversation) {
        stopTyping(selectedConversationData.selectedConversation);
      }
    };
  }, [selectedConversationData?.selectedConversation]);

  // Clear typing users when changing conversations
  useEffect(() => {
    // Stop typing if user switches conversations
    if (isTyping) {
      setIsTyping(false);
    }
    setTypingUsers([]);
    clearTimeout(typingTimeout);
  }, [selectedConversationData?.selectedConversation?.value]);

  const getTypingMessage = () => {
    if (typingUsers.length === 0) return null;
    if (typingUsers.length === 1) return `${typingUsers[0].name} is typing`;
    if (typingUsers.length === 2)
      return `${typingUsers[0].name} and ${typingUsers[1].name} are typing`;
    return `${typingUsers[0].name} and ${
      typingUsers.length - 1
    } others are typing`;
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedConversationData]);

  const removeExistingFile = () => {
    if (existingFile) {
      URL.revokeObjectURL(existingFile.url);
      setExistingFile(null);
    }
  };

  return (
    <>
      <form className="shrink-0 px-4 my-3" onSubmit={(e) => e.preventDefault()}>
        {typingUsers.length > 0 && (
          <div
            // Announced politely so the typing state isn't visual-only. The
            // dots themselves are decorative and stay hidden from the
            // accessibility tree; only the text below is read out.
            aria-live="polite"
            aria-atomic="true"
            className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-full shadow-sm transition-all duration-300 animate-fadeIn mb-2 max-w-[250px]"
          >
            <div className="flex space-x-1" aria-hidden="true">
              <div
                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: "0ms" }}
              ></div>
              <div
                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: "150ms" }}
              ></div>
              <div
                className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                style={{ animationDelay: "300ms" }}
              ></div>
            </div>
            <span className="text-xs font-medium text-gray-700 truncate">
              {getTypingMessage()}
            </span>
          </div>
        )}

        {editingMessage && (
          <div className="flex items-center justify-between bg-yellow-50 p-2 rounded-lg border-l-4 border-yellow-400 mb-2">
            <div className="flex flex-col justify-start items-start">
              <p className="text-sm font-bold text-gray-600">Editing message</p>
              <span className="text-sm text-black">
                {richTextToPlain(editingMessage?.content).slice(0, 50)}
                {richTextToPlain(editingMessage?.content).length > 50 ? "..." : ""}
              </span>
            </div>
            <button
              type="button"
              onClick={handleCancelEdit}
              className="text-gray-500 hover:text-black transition-colors duration-200"
            >
              <AiOutlineClose className="w-4 h-4" />
            </button>
          </div>
        )}

        {replyingTo && !editingMessage && (
          <div className="flex items-center justify-between bg-blue-50 p-2 rounded-lg border-l-4 border-blue-400 mb-2">
            <div className="flex flex-col justify-start items-start ">
              <p className="text-sm font-bold text-gray-500">
                {loggedInUserId === replyingTo?.senderId
                  ? "You"
                  : selectedConversationData?.conversationType === "simple"
                  ? selectedConversationData?.selectedConversation?.label
                  : `${replyingTo?.firstName} ${replyingTo?.lastName}`}
              </p>
              <span className="text-sm text-black">
                {replyingTo?.content
                  ? parseMentions(richTextToPlain(replyingTo.content).slice(0, 50) + (richTextToPlain(replyingTo.content).length > 50 ? "..." : ""))
                  : replyingTo?.fileName}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setReplyingTo(null)}
              className="text-gray-500 hover:text-black transition-colors duration-200"
            >
              <AiOutlineClose className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="relative w-full bg-white p-2 rounded-full flex items-center shadow-[0px_0px_2px_1px_rgba(0,_0,_0,_0.1)]">
          <label
            htmlFor="fileInput"
            className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-gray-600 hover:bg-gray-200 cursor-pointer transition-all drop-shadow-md"
          >
            <CgAttachment className="w-5 h-5" />
          </label>
          <input
            type="file"
            id="fileInput"
            className="hidden"
            onChange={handleFileChange}
            ref={fileInputRef}
          />

          {existingFile && !attachedFile && (
            <div className="flex items-center space-x-2 bg-yellow-50 px-3 py-1 rounded-lg shadow-sm border border-yellow-200">
              {existingFile.file.type.startsWith("image/") ? (
                <img
                  src={existingFile.url || "/placeholder.svg"}
                  alt="Existing file preview"
                  className="w-10 h-10 object-cover rounded"
                />
              ) : (
                <span className="text-sm truncate max-w-[100px]">
                  {existingFile.name}
                </span>
              )}
              <span className="text-xs text-yellow-600">Current</span>
              <button
                type="button"
                onClick={removeExistingFile}
                className="text-yellow-600 hover:text-yellow-800 transition"
              >
                <AiOutlineClose className="w-4 h-4" />
              </button>
            </div>
          )}

          {attachedFile && (
            <div className="flex items-center space-x-2 bg-blue-50 px-3 py-1 rounded-lg shadow-sm">
              {attachedFile.type.startsWith("image/") ? (
                <img
                  src={attachedFilePreviewUrl || "/placeholder.svg"}
                  alt="Preview"
                  className="w-10 h-10 object-cover rounded"
                />
              ) : (
                <span className="text-sm truncate max-w-[100px]">
                  {attachedFile.name}
                </span>
              )}
              {editingMessage && (
                <span className="text-xs text-blue-600">New</span>
              )}
              <button
                type="button"
                onClick={() => {
                  setAttachedFile(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
                className="text-gray-400 hover:text-gray-700 transition"
              >
                <AiOutlineClose className="w-4 h-4" />
              </button>
            </div>
          )}

          <div className="flex-1 min-w-0 mx-2">
            <RichTextInput
              value={message}
              onChange={handleContentChange}
              onKeyDown={handleKeyPress}
              onPaste={handlePaste}
              placeholder="Write here"
              inputRef={inputRef}
              editorApiRef={editorApiRef}
              mentionFormat="idFirst"
              rows={1}
              maxHeight="6rem"
              className="!border-0 !rounded-lg text-sm !text-black focus:!ring-0 focus:!outline-none"
              mentions={filteredParticipants.map((user) => ({
                id: user.userId,
                display: `${user.firstName} ${user.lastName}`,
              }))}
            />
          </div>

          <button
            type="button"
            onClick={() => setShowEmojiPicker((prev) => !prev)}
            className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-gray-600 hover:bg-gray-200 transition-all drop-shadow-md"
          >
            <FiSmile className="w-5 h-5" />
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-all ml-2 drop-shadow-md"
            disabled={loading}
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
            ) : (
              <BsSend className="w-4 h-4" />
            )}
          </button>

          {showEmojiPicker && (
            <div
              className="absolute bottom-12 right-0 z-10"
              ref={emojiPickerRef}
            >
              <EmojiPicker
                theme={isDarkTheme(theme) ? Theme.DARK : Theme.LIGHT}
                onEmojiClick={handleEmojiClick}
                skinTonePickerLocation={null}
                emojiStyle={"google"}
                height={350}
                width={300}
              />
            </div>
          )}
        </div>
      </form>
    </>
  );
};

export default MessageInput;
